/**
 * EDF upload, preview and metadata (`app/services/edf_service.py`).
 *
 * Split out of `brainfocus_api.ts` (FE Arch 1.0, #1165), which re-exports everything
 * here through the `BrainFocusApi` facade. Requests go through `client.request` so tests
 * can stub it on the module namespace.
 */
import * as client from '@brainfocus/web/api/client';
import { assert } from '@brainfocus/web/utils/assert';
import { Npyjs } from '@brainfocus/contrib/npyjs';
import { map, Observable, tap } from 'rxjs';
import { volumeStore } from '@brainfocus/web/api/volume_store';
import * as fflate from 'fflate';
import { buildSignalQuery, SignalQuery } from '@brainfocus/web/sources/signal_query';
import { classifyChannels } from '@brainfocus/web/utils/channel_kind';
import { assertUvMagnitude } from '@brainfocus/web/api/eeg_data';
import { fetchReader, assertPojo } from '@brainfocus/web/api/response';
import type { EdfInfo, RawEdfInfo, RawEdfAnnotation } from '@brainfocus/web/api/types';

/**
 * Upload given edf file and save on backend.
 *
 * Needs to use XHR for progress support.
 *
 * @param payload.file - The EDF file to upload
 * @param payload.patientId - Optional patient ID to associate the upload with
 */
export function uploadEdf(payload: UploadEdfPayload): Observable<client.ResponseProgress<UploadEdfResult>> {
    const formData: Record<string, File | string> = { file: payload.file };
    if (payload.patientId !== undefined) {
        formData['patientId'] = String(payload.patientId);
    }
    if (payload.nativeSfreq !== undefined) {
        formData['nativeSfreq'] = String(payload.nativeSfreq);
    }
    return client.requestWithProgress<{ edf_id: number }>('/api/upload-edf', formData).pipe(
        // A new EDF changes the MRI payloads' consistency report (#183), whichever screen uploaded it.
        tap((v) => {
            if (v.result) volumeStore.invalidateAllMri();
        }),
        map((v) => {
            if (v.result) {
                return {
                    result: { edfId: v.result.edf_id },
                };
            } else {
                return {
                    progress: v.progress,
                };
            }
        }),
    );
}

/**
 * Load raw EEG data directly from an uploaded EDF file for preview.
 * Returns channel data along with metadata (sample rate, annotations).
 */
export function getEdfPreview(
    edfId: number,
    channels: number[],
    query?: SignalQuery,
): Observable<{ progress: number } | EdfPreviewResult> {
    return fetchReader('GET', `/api/get-edf-preview/${edfId}/${channels.join(',')}${buildSignalQuery(query)}`).pipe(
        map((v) => {
            if (v instanceof ArrayBuffer) {
                const unzipd = fflate.unzipSync(new Uint8Array(v));
                assert(unzipd['eeg.npy'], `Missing file: eeg.npy`);
                assert(unzipd['meta.json'], `Missing file: meta.json`);

                const npyjs = new Npyjs();
                const npy = npyjs.parse(unzipd['eeg.npy'].buffer);
                const rows = npy.shape[0] ?? 0;
                const columns = npy.shape[1] ?? 0;
                const data = new Array(rows).fill(null)
                    .map((_, i) => npy.data.subarray(i * columns, (i + 1) * columns));

                const meta = JSON.parse(new TextDecoder().decode(unzipd['meta.json']));

                let channelData: Float32Array[];
                if (unzipd['ranges.json']) {
                    const ranges = JSON.parse(new TextDecoder().decode(unzipd['ranges.json']));
                    channelData = data.map((intRow, i) => {
                        const floatRow = new Float32Array(intRow.length);
                        const intRange = 2 ** (intRow.BYTES_PER_ELEMENT * 8);
                        const floatRange = ranges[i]!;
                        for (let j = 0; j < intRow.length; j++) {
                            floatRow[j] = intRow[j]! / intRange * (floatRange[1] - floatRange[0]) + floatRange[0];
                        }
                        return floatRow;
                    });
                } else {
                    channelData = data;
                }

                assertUvMagnitude(channelData, 'getEdfPreview');
                return {
                    data: channelData,
                    sfreq: meta.sfreq,
                    channelNames: meta.channelNames,
                    annotations: meta.annotations,
                };
            } else {
                return v;
            }
        }),
    );
}

/**
 * Replace the channels marked as Bad in the sEEG Preview of an EDF (#400).
 * Replace semantics: send the FULL new set. Returns the confirmed list in
 * EDF channel order.
 */
export async function setEdfBadChannels(edfId: number, channels: string[]): Promise<string[]> {
    const r = await client.request('PUT', `/api/edfs/${edfId}/bad-channels`, { channels });
    assertPojo<{ bad_channels: string[] }>(r);
    return r.bad_channels;
}

export async function edfInfo(id: number): Promise<EdfInfo> {
    const data = await client.request('POST', `/api/edf-info`, { edf_id: id });
    assertPojo<RawEdfInfo>(data);
    return {
        channelNames: data.meta.ch_names,
        annotations: data.meta.annotations,
        badChannels: data.meta.ch_bads,
        userBadChannels: data.bad_channels ?? [],
        channelKinds: data.channel_kinds ?? classifyChannels(data.meta.ch_names, data.meta.ch_header ?? null),
        date: new Date(data.meta.date),
        length: data.meta.len_sec,
        sfreq: data.meta.sfreq,
        nativeSfreq: data.meta.native_sfreq,
        lastUsed: data.last_used,
        patientId: data.patient_id ?? undefined,
        title: data.title ?? null,
    };
}

export interface UploadEdfPayload {
    file: File;
    patientId?: number;
    /** Native (pre-downsampling) sample rate, when the uploader applied client-side downsampling. */
    nativeSfreq?: number;
}

export interface UploadEdfResult {
    /**
     * Reference to the uploaded EDF used when creating the final recording
     * entry.
     */
    edfId: number;
}

/**
 * Result from EDF preview endpoint containing raw EEG data and metadata.
 */
export interface EdfPreviewResult {
    /** Raw EEG data for each requested channel */
    data: Float32Array[];
    /** Sample rate in Hz */
    sfreq: number;
    /** Channel names for the requested channels */
    channelNames: string[];
    /** Annotations from the EDF file */
    annotations: RawEdfAnnotation[];
}
